import React, { useState, useEffect } from 'react';
import { X, Users, FileText, Weight, Search } from 'lucide-react';
import { useAuth } from '../../../contexts/AuthContext';
import toast from 'react-hot-toast';
import { gymModalInput } from '../gymPageTheme';

export default function AssignTemplateModal({ isOpen, onClose, template, member, onSuccess }) {
  const { getAuthenticatedApi } = useAuth();
  const api = getAuthenticatedApi();
  const [schedaName, setSchedaName] = useState('');
  const [schedaDescription, setSchedaDescription] = useState('');
  const [useMaxWeight, setUseMaxWeight] = useState(false);
  const [members, setMembers] = useState([]);
  const [selectedMembers, setSelectedMembers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingMembers, setLoadingMembers] = useState(true);
  const [memberSearchQuery, setMemberSearchQuery] = useState('');

  useEffect(() => {
    if (isOpen && template) {
      // Precompila nome e descrizione dal template
      setSchedaName(template.name || '');
      setSchedaDescription(template.description || '');
      setUseMaxWeight(false);
      
      // Se è stato passato un membro specifico, pre-selezionalo
      if (member) {
        //console.log('[ASSIGN_MODAL] Pre-selecting member:', member);
        setSelectedMembers([member.id]);
      } else {
        setSelectedMembers([]);
      }
      
      // Reset ricerca
      setMemberSearchQuery('');
      
      fetchMembers();
    }
  }, [isOpen, template, member]);

  const fetchMembers = async () => {
    try {
      setLoadingMembers(true);
      //console.log('[ASSIGN_MODAL] Fetching members...');
      
      const response = await api.get('/gyms.php?action=members');
      
      //console.log('[ASSIGN_MODAL] Response membri:', response.data);
      
      // Filtra solo membri attivi con ruolo 'member'
      let activeMembers = [];
      
      if (Array.isArray(response.data)) {
        activeMembers = response.data.filter(m => 
          (m.active === 1 || m.active === '1') && 
          (m.role_name === 'user' || m.role_in_gym === 'member')
        );
      }
      
      //console.log('[ASSIGN_MODAL] Membri attivi trovati:', activeMembers.length, activeMembers);
      setMembers(activeMembers);
    } catch (error) {
      console.error('[ASSIGN_MODAL] Errore nel caricamento membri:', error);
      setMembers([]);
    } finally {
      setLoadingMembers(false);
    }
  };

  const toggleMember = (memberId) => {
    setSelectedMembers(prev => 
      prev.includes(memberId)
        ? prev.filter(id => id !== memberId)
        : [...prev, memberId]
    );
  };

  const selectAll = () => {
    // Seleziona solo i membri filtrati (visibili)
    const visibleMemberIds = filteredMembers.map(m => m.id);
    setSelectedMembers(prev => {
      // Aggiungi i membri filtrati a quelli già selezionati
      const combined = new Set([...prev, ...visibleMemberIds]);
      return Array.from(combined);
    });
  };

  const deselectAll = () => {
    setSelectedMembers([]);
  };

  // Filtra membri in base alla ricerca
  const filteredMembers = members.filter(member => {
    if (!memberSearchQuery.trim()) return true;
    
    const query = memberSearchQuery.toLowerCase();
    const name = (member.name || '').toLowerCase();
    const username = (member.username || '').toLowerCase();
    const email = (member.email || '').toLowerCase();
    
    return name.includes(query) || username.includes(query) || email.includes(query);
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!schedaName.trim()) {
      toast.error('Inserisci un nome per la scheda');
      return;
    }
    
    if (selectedMembers.length === 0) {
      toast.error('Seleziona almeno un membro');
      return;
    }

    try {
      setLoading(true);
      
      const data = {
        template_id: template.id,
        member_ids: selectedMembers,
        scheda_name: schedaName.trim(),
        scheda_description: schedaDescription.trim() || null,
        use_max_weight: useMaxWeight
      };

      const response = await api.post('/gym_workout_templates.php?action=create_workout', data);
      
      if (response.data.success) {
        toast.success(response.data.message || 'Schede assegnate con successo!');
        onSuccess();
        onClose();
      }
    } catch (error) {
      console.error('Errore nell\'assegnazione:', error);
      toast.error(error.response?.data?.error || 'Errore nell\'assegnazione del template');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm">
      <div className="flex max-h-[90vh] w-full max-w-2xl flex-col overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-2xl dark:border-white/10 dark:bg-gray-900 dark:shadow-black/35">
        {/* Header */}
        <div className="flex shrink-0 items-center justify-between border-b border-gray-200 bg-[#dad8e5] px-6 py-4 dark:border-white/10 dark:bg-gray-800">
          <div className="min-w-0 pr-2">
            <h2 className="flex items-center gap-2 text-xl font-bold text-gray-900 dark:font-open-sans dark:text-white">
              <Users className="h-5 w-5 shrink-0" />
              Assegna Template
            </h2>
            <p className="mt-1 truncate text-sm text-gray-600 dark:text-white/75">
              {template?.name}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="shrink-0 rounded-lg p-2 text-gray-600 transition-colors hover:bg-black/5 dark:text-white/90 dark:hover:bg-white/10"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Body */}
        <form onSubmit={handleSubmit} className="flex min-h-0 flex-1 flex-col overflow-y-auto">
          <div className="space-y-6 bg-white p-6 dark:bg-gray-900">
            {/* Nome Scheda */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-white/85 mb-2">
                <FileText className="h-4 w-4 inline mr-1" />
                Nome Scheda *
              </label>
              <input
                type="text"
                value={schedaName}
                onChange={(e) => setSchedaName(e.target.value)}
                className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-gray-900 focus:border-transparent focus:ring-2 focus:ring-indigo-500 dark:border-gray-700 dark:bg-gray-800 dark:text-white dark:focus:ring-indigo-500/30"
                placeholder="Es: Forza Full Body"
                required
              />
            </div>

            {/* Descrizione */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-white/85 mb-2">
                Descrizione (opzionale)
              </label>
              <textarea
                value={schedaDescription}
                onChange={(e) => setSchedaDescription(e.target.value)}
                rows={3}
                className="w-full resize-none rounded-lg border border-gray-300 bg-white px-3 py-2 text-gray-900 focus:border-transparent focus:ring-2 focus:ring-indigo-500 dark:border-gray-700 dark:bg-gray-800 dark:text-white dark:focus:ring-indigo-500/30"
                placeholder="Descrizione della scheda..."
              />
            </div>

            {/* Pesi Suggeriti */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-white/85 mb-3">
                <Weight className="h-4 w-4 inline mr-1" />
                Pesi Suggeriti
              </label>
              <div className="space-y-2">
                <label className="flex cursor-pointer items-start rounded-lg border border-gray-300 p-3 transition-colors hover:bg-gray-50 dark:border-white/15 dark:hover:bg-white/5">
                  <input
                    type="radio"
                    checked={!useMaxWeight}
                    onChange={() => setUseMaxWeight(false)}
                    className="h-4 w-4 text-indigo-600 mt-0.5"
                  />
                  <div className="ml-3 flex-1">
                    <span className="text-sm font-medium text-gray-900 dark:text-white block">
                      Usa peso suggerito dal template
                    </span>
                    <span className="text-xs text-gray-500 dark:text-white/65">
                      Il peso configurato nel template (consigliato per principianti)
                    </span>
                  </div>
                </label>
                <label className="flex cursor-not-allowed items-start rounded-lg border border-gray-300 p-3 opacity-50 dark:border-white/15">
                  <input
                    type="radio"
                    checked={useMaxWeight}
                    onChange={() => setUseMaxWeight(true)}
                    disabled
                    className="h-4 w-4 text-indigo-600 mt-0.5"
                  />
                  <div className="ml-3 flex-1">
                    <span className="text-sm font-medium text-gray-900 dark:text-white block">
                      Usa peso massimo (Coming Soon)
                    </span>
                    <span className="text-xs text-gray-500 dark:text-white/65">
                      Calcola peso in base al numero massimo di ripetizioni
                    </span>
                  </div>
                </label>
              </div>
            </div>

            {/* Selezione Membri */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <label className="block text-sm font-medium text-gray-700 dark:text-white/85">
                  <Users className="h-4 w-4 inline mr-1" />
                  Assegna a Membri * ({selectedMembers.length} selezionati
                  {memberSearchQuery && filteredMembers.length !== members.length && 
                    ` - ${filteredMembers.length} di ${members.length} visibili`})
                </label>
                <div className="space-x-2">
                  <button
                    type="button"
                    onClick={selectAll}
                    className="text-xs text-indigo-600 dark:text-indigo-400 hover:underline"
                  >
                    Seleziona tutti
                  </button>
                  <button
                    type="button"
                    onClick={deselectAll}
                    className="text-xs text-gray-600 dark:text-white/65 hover:underline"
                  >
                    Deseleziona tutti
                  </button>
                </div>
              </div>

              {/* Filtro ricerca membri */}
              {members.length > 0 && (
                <div className="mb-3 relative">
                  <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 transform text-gray-400 dark:text-white/45" />
                  <input
                    type="text"
                    value={memberSearchQuery}
                    onChange={(e) => setMemberSearchQuery(e.target.value)}
                    placeholder="Cerca membro per nome, username o email..."
                    className={`${gymModalInput} pl-10 pr-4`}
                  />
                </div>
              )}

              {loadingMembers ? (
                <div className="text-center py-8">
                  <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
                  <p className="text-sm text-gray-600 dark:text-white/65 mt-2">Caricamento membri...</p>
                </div>
              ) : filteredMembers.length === 0 ? (
                <div className="rounded-lg bg-gray-50 py-8 text-center dark:bg-gray-900/50">
                  <Users className="mx-auto mb-2 h-12 w-12 text-gray-400 dark:text-white/40" />
                  <p className="text-gray-600 dark:text-white/65">
                    {memberSearchQuery 
                      ? `Nessun membro trovato per "${memberSearchQuery}"`
                      : 'Nessun membro trovato'}
                  </p>
                  {memberSearchQuery && members.length > 0 && (
                    <button
                      type="button"
                      onClick={() => setMemberSearchQuery('')}
                      className="mt-2 text-xs text-indigo-600 dark:text-indigo-400 hover:underline"
                    >
                      Pulisci filtro
                    </button>
                  )}
                </div>
              ) : (
                <div className="max-h-64 overflow-y-auto rounded-lg border border-gray-300 dark:border-white/15">
                  {filteredMembers.map((member) => (
                    <label
                      key={member.id}
                      className="flex cursor-pointer items-center border-b border-gray-200 p-3 last:border-b-0 hover:bg-gray-50 dark:border-white/10 dark:hover:bg-white/5"
                    >
                      <input
                        type="checkbox"
                        checked={selectedMembers.includes(member.id)}
                        onChange={() => toggleMember(member.id)}
                        className="h-4 w-4 text-indigo-600 rounded"
                      />
                      <div className="ml-3 flex-1">
                        <p className="text-sm font-medium text-gray-900 dark:text-white">
                          {member.name || member.username}
                        </p>
                        {member.email && (
                          <p className="text-xs text-gray-500 dark:text-white/65">
                            {member.email}
                          </p>
                        )}
                      </div>
                    </label>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Footer */}
          <div className="flex shrink-0 justify-end gap-3 border-t border-gray-200 bg-gray-50 p-6 dark:border-white/10 dark:bg-gray-800">
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg bg-white px-4 py-2 font-medium text-gray-700 transition-colors hover:bg-gray-100 disabled:opacity-50 dark:bg-gray-800 dark:text-white dark:hover:brightness-110"
              disabled={loading}
            >
              Annulla
            </button>
            <button
              type="submit"
              disabled={loading || selectedMembers.length === 0}
              className="flex items-center gap-2 rounded-lg bg-indigo-600 px-4 py-2 text-white transition-colors hover:bg-indigo-700 disabled:cursor-not-allowed disabled:bg-gray-400 dark:bg-indigo-600 dark:text-white dark:hover:brightness-110 dark:disabled:opacity-50"
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  Assegnazione...
                </>
              ) : (
                <>
                  <Users className="h-4 w-4" />
                  Assegna Scheda
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}










